// src/index.jsx
// Single owner of service-worker registration.
// UpdateBanner uses navigator.serviceWorker.getRegistration() to re-use this.
import React from 'react';
import ReactDOM from 'react-dom/client';
import './index.css';
import App from './App';

// ── iOS :active state shim ───────────────────────────────────────────────
// WKWebView (installed PWA / wrapped APK / iOS Safari) does not apply the
// CSS :active pseudo-class anywhere in the document unless a touchstart
// listener is registered. This listener is intentionally a no-op — it only
// exists to opt iOS into :active handling app-wide.
document.addEventListener('touchstart', function () {}, true);

// ── Global Error Handlers ────────────────────────────────────────────────────
window.addEventListener('error', (event) => {
  console.error('[Global Error Handler]', event.error || event.message);
});

window.addEventListener('unhandledrejection', async (event) => {
  const reason = event.reason;
  // AbortError is almost always benign — it fires when a fetch is cancelled
  // because the user navigated away, a component unmounted, or the service
  // worker lifecycle aborted a request in-flight. Logging it as an unhandled
  // rejection error creates noise. Swallow it.
  if (
    reason?.name === 'AbortError' ||
    (reason instanceof DOMException && reason.code === 20)
  ) {
    event.preventDefault();
    return;
  }
  // ── Chunk-load recovery ─────────────────────────────────────────────────
  // After a Cloudflare deploy, the active SW may serve a stale index.html
  // referencing old chunk hashes that no longer exist on CDN. The dynamic
  // import() for a lazy page (e.g. BibleStudyGuide) then throws this error.
  // Fix: clear all SW caches and hard-reload so the browser fetches fresh
  // HTML with current chunk hashes. The sw.js NavigationRoute is now
  // NetworkFirst, so the reload will bypass the stale shell.
  if (/Failed to fetch dynamically imported module|Importing a module script failed/i.test(reason?.message ?? '')) {
    console.warn('[Chunk Recovery] Stale chunk detected — clearing caches and reloading.');
    try {
      if ('serviceWorker' in navigator) {
        const regs = await navigator.serviceWorker.getRegistrations();
        await Promise.all(regs.map((r) => r.unregister()));
      }
      if ('caches' in window) {
        const keys = await caches.keys();
        await Promise.all(keys.map((k) => caches.delete(k)));
      }
    } finally {
      // SW unregistered + caches cleared — reload fetches fresh HTML directly
      // from Cloudflare, bypassing any stale SW precache entirely.
      window.location.reload();
    }
    return;
  }

  console.error('[Unhandled Promise Rejection]', reason);
  event.preventDefault();
});

const root = ReactDOM.createRoot(document.getElementById('root'));
root.render(
  <React.StrictMode>
    <App />
  </React.StrictMode>
);

// ── Service Worker Registration (single source of truth) ─────────────────────
// UpdateBanner.jsx calls navigator.serviceWorker.getRegistration('/') to re-use
// this registration — it does NOT call register() again.
if ('serviceWorker' in navigator) {
  window.addEventListener('load', async () => {
    // ── ONE-TIME CLEANUP: Unregister the legacy CRA service worker ────────────
    // The old 'service-worker.js' (CRA-era) was accidentally shipped in the
    // Vite build output. This block silently unregisters any registration whose
    // scriptURL ends in 'service-worker.js'. It runs once on each page load and
    // is a no-op once all clients have been cleaned up.
    try {
      const allRegistrations = await navigator.serviceWorker.getRegistrations();
      for (const reg of allRegistrations) {
        if (reg.active?.scriptURL?.endsWith('service-worker.js')) {
          await reg.unregister();
          console.log('[SW] Unregistered legacy CRA service worker:', reg.active.scriptURL);
        }
      }
    } catch (err) {
      console.warn('[SW] Legacy SW cleanup failed (non-fatal):', err);
    }

    // ── Register (or update) the current Vite/Workbox SW ─────────────────────
    navigator.serviceWorker.getRegistration('/').then((existing) => {
      if (existing) {
        console.log('[SW] Already registered:', existing.scope);
        // Proactively check for a new SW version on every page load.
        existing.update().catch((err) => {
          // FIX: Discriminate between offline (benign) and script evaluation
          // errors (actionable). A TypeError here almost always means the new
          // sw.js failed to parse/evaluate in the browser — check the build
          // output for raw ES import statements or an unresolved __WB_MANIFEST.
          if (err?.name === 'AbortError') return; // navigation cancelled — benign
          if (!navigator.onLine) {
            // Silent: update() always fails when offline — not worth logging
            return;
          }
          // Online + non-Abort error → likely a bad SW build. Log with enough
          // context to diagnose from a bug report.
          console.warn('[SW] Update check failed:', err?.name, err?.message);
        });
        return;
      }
      navigator.serviceWorker
        .register('/sw.js')
        .then((registration) => {
          console.log('[SW] Registered:', registration.scope);
        })
        .catch((error) => {
          if (error?.name !== 'AbortError') {
            console.warn('[SW] Registration failed:', error);
          }
        });
    });
  });
}